import * as THREE from "three";
import { Observable } from "rxjs";
import { component, effects, emit, on, Op, set, store } from "@apendua/signal";
import { dom, dropdown } from "@apendua/freedom";
import type { AudioOutputSelection } from "@apendua.dev/sound";
import { diagnosticsButton, type DiagnosticsControls } from "@apendua.dev/diagnostics";
import { children, $, text } from "@apendua/justdom";
import {
  actionButton,
  banner,
  hint,
  levelCaption,
  levelValue,
  musicRow,
  outputClasses,
  panelCard,
  panelTitle,
  sectionTitle,
  primaryButton,
  statRow,
  stepButton,
  stepValue,
  UI_SCALE,
} from "./styles.js";

export interface StatusUIProps {
  position: THREE.Vector3;
  rotation: THREE.Euler;
  /** How many pieces the sphere is currently cut into. */
  pieces: number;
  /** How many separate clumps are still floating around. */
  clusters: number;
  /** Seconds since this puzzle was dealt, frozen once it is solved. */
  seconds: number;
  solved: boolean;
  /** How loud the soundtrack is, from 0 to 1. */
  volume: number;
}

export interface StatusUIEvents {
  /** Deal the same number of pieces again, freshly cut and scattered. */
  shuffle: void;
  /** Cut the sphere into one more piece than it is now. */
  addPiece: void;
  /** Back to a single, uncut piece. */
  startOver: void;
  quieter: void;
  louder: void;
}

const defaults: StatusUIProps = {
  position: new THREE.Vector3(),
  rotation: new THREE.Euler(),
  pieces: 1,
  clusters: 1,
  seconds: 0,
  solved: true,
  volume: 0,
};

function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${minutes}:${rest.toString().padStart(2, "0")}`;
}

/**
 * The only flat surface in the scene: what the puzzle currently is, how far
 * through it the player is, and the three ways of starting again. Everything
 * here is also reachable by poking the polytope itself, but nothing about that
 * is discoverable, which is what this panel is for.
 */
const StatusUI = component(
  (
    ctx: WeakKey,
    /** Where the soundtrack goes, which the picker below both shows and sets. */
    output: AudioOutputSelection,
    /** The frame rate panel, which the button under the music row switches. */
    diagnostics: DiagnosticsControls,
    operators: Op<StatusUIProps, StatusUIEvents>,
  ) => {
    const props = store<StatusUIProps, StatusUIEvents>(defaults, operators);

    const button = (
      className: Observable<string>,
      label: () => string,
      event: keyof StatusUIEvents,
    ) =>
      $("div", [
        set("className", className),
        on("click", () => {
          emit(props, event, undefined);
        }),
        children(text`${label}`),
      ]);

    return dom(
      ctx,
      $("div", [
        set("className", panelCard),
        children(
          $("div", [set("className", panelTitle), children(text`Polytope XR`)]),
          $("div", [set("className", levelValue), children(text`${() => props.pieces}`)]),
          $("div", [
            set("className", levelCaption),
            children(text`${() => (props.pieces === 1 ? "piece" : "pieces")}`),
          ]),
          $("div", [
            set("className", statRow),
            children(
              text`Loose`,
              $("span", [children(text`${() => `${props.clusters} / ${props.pieces}`}`)]),
            ),
          ]),
          $("div", [
            set("className", statRow),
            children(text`Time`, $("span", [children(text`${() => formatTime(props.seconds)}`)])),
          ]),
          // Rendered conditionally rather than hidden with `display`: the portal
          // that projects this DOM into the scene watches for added and removed
          // nodes, not for inline style changes.
          component(() => {
            if (!props.solved) {
              return text``;
            }
            return $("div", [set("className", banner), children(text`Solved`)]);
          })(),
          // Solving is what earns the next piece, so that becomes the obvious
          // move the moment it is available; until then the obvious move is to
          // deal the same puzzle again.
          component(() => {
            if (props.solved) {
              return button(primaryButton, () => "Add a piece", "addPiece");
            }
            return button(primaryButton, () => "Shuffle", "shuffle");
          })(),
          component(() => {
            if (!props.solved) {
              return text``;
            }
            return button(actionButton, () => "Shuffle", "shuffle");
          })(),
          button(actionButton, () => "Start over", "startOver"),
          $("div", [set("className", sectionTitle), children(text`Music`)]),
          $("div", [
            set("className", musicRow),
            children(
              button(stepButton, () => "-", "quieter"),
              $("div", [
                set("className", stepValue),
                children(text`${() => `${Math.round(props.volume * 100)}%`}`),
              ]),
              button(stepButton, () => "+", "louder"),
            ),
          ]),
          // Nothing to switch between is nothing to show: most headsets have one
          // way out for sound, and it is only a desktop with a headset plugged in
          // next to its speakers that has a choice worth making.
          component(() => {
            if (output.outputs.get().length < 2) {
              return text``;
            }
            return dropdown({
              options: () =>
                output.outputs.get().map(({ deviceId, label }) => ({ value: deviceId, label })),
              classes: outputClasses,
              placeholder: "Browser's choice",
              selected: () => output.deviceId.get(),
              onSelect: (deviceId) => output.select(deviceId),
            });
          })(),
          diagnosticsButton(diagnostics, actionButton),
          $("div", [
            set("className", hint),
            children(
              text`Trigger drags a piece, grip pulls the whole cluster. Line one up with where it belongs and let go — it snaps.`,
            ),
          ]),
        ),
      ]),
      [
        effects((obj) => {
          obj.position.copy(props.position);
          obj.rotation.copy(props.rotation);
          obj.scale.setScalar(UI_SCALE);
        }),
      ],
    );
  },
);

export default StatusUI;
